import {
  useCallback,
  useEffect,
  useState,
} from 'react'
import axios from 'axios'
import { notifications } from '@mantine/notifications'
import { IconCheck } from '@tabler/icons-react'

import {
  ValveRecoveryPolicy,
  VALVE_FEATURE_MIN_FIRMWARE,
  VALVE_RECOVERY_POLICY_META,
} from '@data/device/model/device'
import {
  useValveProbe,
  useValveRecoveryPolicyUpdate,
  type ValveTarget,
  valveTargetKey,
} from '@data/device/device'
import { describeApiError } from '@common/utils/apiErrors'
import {
  formatFirmwareVersion,
  isFirmwareAtLeast,
} from '@common/utils/firmwareVersion'

// Shared by the Device Modal's Valves tab, the Test Bench modal's Valves tab and the
// standalone Operate Valves page — all stage a recovery-mode edit locally and offer the
// on-demand valve probe from the same panel, since a probe result also carries an
// authoritative recovery policy.
export const useValveRecoveryPolicy = (
  target: ValveTarget,
  devicePolicy: ValveRecoveryPolicy | null,
  firmware: string,
) => {
  const { mutate: saveRecoveryPolicy, isPending: isSavingPolicy } = useValveRecoveryPolicyUpdate()
  const { mutate: probeValves, isPending: isProbing } = useValveProbe()

  const isFirmwareSupported = isFirmwareAtLeast(firmware, VALVE_FEATURE_MIN_FIRMWARE)
  const minFirmwareVersion = formatFirmwareVersion(VALVE_FEATURE_MIN_FIRMWARE)

  // Selecting Auto (-1) can resolve to a different policy, so the PATCH/probe response is
  // the baseline afterwards rather than the value that was sent.
  const [savedPolicy, setSavedPolicy] = useState<ValveRecoveryPolicy | null>(null)
  const [policyDraft, setPolicyDraft] = useState<ValveRecoveryPolicy>()
  const [probeConfirming, setProbeConfirming] = useState(false)

  // Drafts are per-device — never carry one device's staged edits onto another. Keyed on the
  // string, not the target object, so a caller building the target inline cannot reset the
  // draft on every render.
  const targetKey = valveTargetKey(target)

  useEffect(() => {
    setPolicyDraft(undefined)
    setSavedPolicy(null)
  }, [targetKey])

  const recoveryPolicy = savedPolicy ?? devicePolicy
  const selectedPolicy = policyDraft ?? recoveryPolicy
  const isPolicyModified = policyDraft !== undefined

  const handlePolicyChange = useCallback((value: string | null) => {
    if (value === null) return
    const next = Number(value) as ValveRecoveryPolicy
    setPolicyDraft(next === recoveryPolicy ? undefined : next)
  }, [recoveryPolicy])

  const handleDiscardPolicy = useCallback(() => {
    setPolicyDraft(undefined)
  }, [])

  const handleSavePolicy = useCallback(() => {
    if (policyDraft === undefined) return

    saveRecoveryPolicy(
      {
        target,
        policy: policyDraft,
      },
      {
        onSuccess: (updated) => {
          setSavedPolicy(updated.policy)
          setPolicyDraft(undefined)
          notifications.show({
            title: 'Recovery mode updated',
            message: `Valve recovery mode set to ${VALVE_RECOVERY_POLICY_META[updated.policy].label}.`,
            color: 'green',
            icon: <IconCheck size={16} />,
          })
        },
        onError: (error) => {
          notifications.show({
            title: 'Unable to set recovery mode',
            message:
              axios.isAxiosError(error) && error.response?.status === 422
                ? 'The recovery policy is invalid. Select one of the available modes.'
                : describeApiError(error, 'Failed to set the valve recovery mode.'),
            color: 'red',
          })
        },
      },
    )
  }, [target, policyDraft, saveRecoveryPolicy])

  const handleProbe = useCallback(() => {
    setProbeConfirming(false)

    probeValves(target, {
      onSuccess: ({ valveRecovery }) => {
        // The probe reports the policy in force at probe time — treat it as authoritative
        // for the same reason the PATCH response is.
        setSavedPolicy(valveRecovery)
        notifications.show({
          title: 'Valve probe complete',
          message: 'Valve positions have been re-read from the device.',
          color: 'green',
          icon: <IconCheck size={16} />,
        })
      },
      onError: (error) => {
        notifications.show({
          title: 'Valve probe failed',
          message:
            describeApiError(error, 'The device may be mid-travel or commissioning.'),
          color: 'red',
        })
      },
    })
  }, [target, probeValves])

  return {
    selectedPolicy,
    isPolicyModified,
    isSavingPolicy,
    handlePolicyChange,
    handleDiscardPolicy,
    handleSavePolicy,
    probeConfirming,
    setProbeConfirming,
    handleProbe,
    isProbing,
    isFirmwareSupported,
    minFirmwareVersion,
  }
}
